import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Gauge, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Global rate-limit config for SMB outreach automation:
 *   • daily_cap        — max messages dispatched per IST calendar day
 *   • per_tick_limit   — max messages dispatched per scheduler tick (~60s)
 *
 * Today's live counter is shown so admins can see remaining budget.
 */
export const SmbOutreachThrottleCard = () => {
  const [doc, setDoc] = useState(null);
  const [dailyCap, setDailyCap] = useState('');
  const [perTick, setPerTick] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/smb/outreach/automation/throttle');
      if (res.data.success) {
        setDoc(res.data.data);
        setDailyCap(String(res.data.data.daily_cap ?? ''));
        setPerTick(String(res.data.data.per_tick_limit ?? ''));
      }
    } catch { toast.error('Failed to load throttle config'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const cap = parseInt(dailyCap, 10);
    const tick = parseInt(perTick, 10);
    if (!Number.isFinite(cap) || cap < 1 || cap > 10000) {
      toast.error('Daily cap must be between 1 and 10,000'); return;
    }
    if (!Number.isFinite(tick) || tick < 1 || tick > 500) {
      toast.error('Per-tick limit must be between 1 and 500'); return;
    }
    setSaving(true);
    try {
      const res = await api.put('/smb/outreach/automation/throttle', {
        daily_cap: cap, per_tick_limit: tick,
      });
      if (res.data.success) {
        setDoc(res.data.data);
        toast.success(res.data.message || 'Throttle updated');
      }
    } catch { toast.error('Failed to save throttle config'); }
    finally { setSaving(false); }
  };

  const todaySent = doc?.today_sent ?? 0;
  const remaining = doc?.remaining_today ?? 0;
  const cap = doc?.daily_cap ?? 0;
  const pctUsed = cap > 0 ? Math.min(100, Math.round((todaySent / cap) * 100)) : 0;

  return (
    <Card data-testid="smb-outreach-throttle-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Gauge className="h-5 w-5 text-emerald-500" />Outreach Rate Limits
          {!loading && (
            <Badge variant={remaining > 0 ? 'default' : 'destructive'}
                   className="ml-2 text-[10px] uppercase tracking-wide"
                   data-testid="smb-outreach-throttle-status">
              {todaySent}/{cap} today · {remaining} left
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Caps applied on top of the per-lead schedule (max 3 messages, 10 AM – 6 PM IST window).
          Prevents bursting through thousands of queued leads in one window.
          The scheduler ticks every <span className="font-mono">60s</span>; <strong>per-tick limit</strong> controls how many are
          dispatched each tick, and the <strong>daily cap</strong> is the hard ceiling per IST calendar day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Live usage bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Today’s usage</span>
            <span data-testid="smb-outreach-throttle-usage">{todaySent} / {cap} ({pctUsed}%)</span>
          </div>
          <div className="h-2 rounded bg-muted overflow-hidden">
            <div
              className={`h-full transition-all ${pctUsed >= 100 ? 'bg-red-500' : pctUsed >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${pctUsed}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="smb-throttle-daily-cap">Daily cap (messages / day, IST)</Label>
            <Input
              id="smb-throttle-daily-cap"
              type="number" min={1} max={10000}
              value={dailyCap}
              onChange={(e) => setDailyCap(e.target.value)}
              disabled={loading || saving}
              data-testid="smb-outreach-throttle-daily-cap-input"
            />
            <p className="text-xs text-muted-foreground">
              Hard ceiling; once reached the scheduler defers remaining jobs to the next day.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="smb-throttle-per-tick">Per-tick limit (messages / 60s)</Label>
            <Input
              id="smb-throttle-per-tick"
              type="number" min={1} max={500}
              value={perTick}
              onChange={(e) => setPerTick(e.target.value)}
              disabled={loading || saving}
              data-testid="smb-outreach-throttle-per-tick-input"
            />
            <p className="text-xs text-muted-foreground">
              Smooths bursts. e.g. <span className="font-mono">10</span> → max ~600/hour.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            {doc?.updated_at
              ? <>Last changed {new Date(doc.updated_at).toLocaleString()}</>
              : 'Defaults in use'}
          </p>
          <div className="flex items-center gap-2">
            {(loading || saving) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Button
              onClick={save}
              disabled={loading || saving}
              data-testid="smb-outreach-throttle-save-button"
            >Save</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
